import { faCopy, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import React, { useContext, useState } from 'react';

import AppContext from '@/contexts/appContext';
import PersonAvailabilityRepository from '@/repositories/PersonAvailabilityRepository';
import { TimeInterval } from '@/types/personAvailability';
import Button from '@/ui/components/Button';
import { WEEKDAY_LABELS, WEEKDAY_ORDER } from '@/utils/calendar';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { convertTo24Hour, validateNoOverlappingIntervals } from '@/utils/timeFormat';
import { hasPermission, PEOPLE_AVAILABILITY_CHANGE } from '@/utils/userPermission';

import CopyTimesModal from '../CopyTimesModal';
import TimeIntervalInput from '../TimeIntervalInput';

import type { Props } from './types';

const WeeklyHoursSection: React.FC<Props> = ({
  rules,
  availabilityId,
  personId,
  timezone,
  onUpdate
}) => {
  const { addMessage, permissions } = useContext(AppContext);
  const canMutate = hasPermission({ permissions, requiredPermission: PEOPLE_AVAILABILITY_CHANGE });

  const [copyFromWeekday, setCopyFromWeekday] = useState<string | null>(null);
  const [editingIntervals, setEditingIntervals] = useState<{ [weekday: string]: TimeInterval[] }>(
    {}
  );
  const [intervalErrors, setIntervalErrors] = useState<{ [weekday: string]: string | null }>({});
  const availabilityRepo = PersonAvailabilityRepository();

  const getRuleForWeekday = (weekday: string) => {
    return rules.find((rule) => rule.weekday === weekday);
  };

  const getIntervalsForWeekday = (weekday: string): TimeInterval[] => {
    if (editingIntervals[weekday]) {
      return editingIntervals[weekday];
    }
    const rule = getRuleForWeekday(weekday);
    return rule?.intervals || [];
  };

  const handleAddInterval = async (weekday: string) => {
    const currentIntervals = getIntervalsForWeekday(weekday);
    const updatedIntervals = [...currentIntervals, { from: '9:00am', to: '5:00pm' }];
    setEditingIntervals({
      ...editingIntervals,
      [weekday]: updatedIntervals
    });
    await saveWeekdayRule(weekday, updatedIntervals);
  };

  const handleRemoveInterval = async (weekday: string, index: number) => {
    const currentIntervals = getIntervalsForWeekday(weekday);
    const newIntervals = currentIntervals.filter((_, i) => i !== index);

    setEditingIntervals({
      ...editingIntervals,
      [weekday]: newIntervals
    });

    await saveWeekdayRule(weekday, newIntervals);
  };

  const handleIntervalChange = async (
    weekday: string,
    index: number,
    field: 'from' | 'to',
    value: string
  ) => {
    const currentIntervals = getIntervalsForWeekday(weekday);
    const newIntervals = [...currentIntervals];
    newIntervals[index] = { ...newIntervals[index], [field]: value };

    setEditingIntervals({
      ...editingIntervals,
      [weekday]: newIntervals
    });

    await saveWeekdayRule(weekday, newIntervals);
  };

  const saveWeekdayRule = async (weekday: string, intervals: TimeInterval[]) => {
    // Validate no overlapping intervals
    const overlapError = validateNoOverlappingIntervals(intervals);
    setIntervalErrors((prev) => ({ ...prev, [weekday]: overlapError }));
    if (overlapError) {
      return false;
    }

    setIntervalErrors((prev) => {
      const next = { ...prev };
      delete next[weekday];
      return next;
    });

    const otherRules = rules.filter((r) => r.weekday !== weekday);

    // Convert intervals to 24-hour format for API
    const formattedIntervals = intervals.map((interval) => ({
      from: convertTo24Hour(interval.from),
      to: convertTo24Hour(interval.to)
    }));

    const updatedRules = [
      ...otherRules,
      ...(formattedIntervals.length > 0
        ? [{ type: 'weekday' as const, weekday: weekday, intervals: formattedIntervals }]
        : [])
    ];

    try {
      if (availabilityId) {
        await availabilityRepo.update({
          id: availabilityId,
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: updatedRules
          }
        });
      } else {
        await availabilityRepo.create({
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: updatedRules
          }
        });
      }
      onUpdate();
      return true;
    } catch (error) {
      const messages = mapAxiosErrorMessages(error as AxiosError);
      messages.forEach((msg) => addMessage(msg.level, msg.text));
      return false;
    }
  };

  const handleCopyComplete = () => {
    setCopyFromWeekday(null);
    // Clear editing intervals cache to force component to use fresh rules data
    setEditingIntervals({});
    onUpdate();
  };

  return (
    <div>
      <h2 className="flex items-center gap-2 text-lg font-semibold mb-4">Weekly Hours</h2>
      <p className="text-sm text-gray-600 mb-4">
        Set the member&apos;s recurring weekly availability. You can add multiple time intervals per
        day or mark a day as unavailable by removing all intervals.
      </p>

      <div className="space-y-3">
        {WEEKDAY_ORDER.map((weekday) => {
          const intervals = getIntervalsForWeekday(weekday);
          const isUnavailable = intervals.length === 0;

          return (
            <div key={weekday} className="p-3 bg-gray-50 rounded">
              <div className="flex items-start gap-2 sm:gap-3">
                <div
                  className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-semibold text-white ${
                    isUnavailable ? 'bg-gray-400' : 'bg-blue-600'
                  }`}>
                  {WEEKDAY_LABELS[weekday]}
                </div>
                <div className="flex-1 min-w-0">
                  {isUnavailable ? (
                    <div className="h-8 flex items-center">
                      <span className="text-gray-500">Unavailable</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {intervals.map((interval, idx) => (
                        <TimeIntervalInput
                          key={idx}
                          interval={interval}
                          onFromChange={(value) =>
                            handleIntervalChange(weekday, idx, 'from', value)
                          }
                          onToChange={(value) => handleIntervalChange(weekday, idx, 'to', value)}
                          onRemove={() => handleRemoveInterval(weekday, idx)}
                        />
                      ))}
                      {intervalErrors[weekday] && (
                        <p className="text-xs text-red-600 mt-2">{intervalErrors[weekday]}</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 sm:gap-2 shrink-0">
                  {canMutate && (
                    <>
                      {intervals.length > 0 && (
                        <Button
                          onClick={() => setCopyFromWeekday(weekday)}
                          ariaLabel="Copy to other days"
                          size="sm">
                          <FontAwesomeIcon icon={faCopy} />
                        </Button>
                      )}
                      <Button
                        onClick={() => handleAddInterval(weekday)}
                        ariaLabel="Add time interval"
                        size="sm">
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {copyFromWeekday && (
        <CopyTimesModal
          sourceWeekday={copyFromWeekday}
          sourceIntervals={getIntervalsForWeekday(copyFromWeekday)}
          allRules={rules}
          availabilityId={availabilityId}
          personId={personId}
          timezone={timezone}
          onClose={() => setCopyFromWeekday(null)}
          onComplete={handleCopyComplete}
        />
      )}
    </div>
  );
};

export default React.memo(WeeklyHoursSection);
